import type { WorkbookProfile } from '../../types/database.ts';
import type { LearningReasonCode } from '../../constants/languages.ts';
import { SITUATIONS } from '../../constants/languages.ts';
import { safeText } from '../../sanitize.ts';

// =============================================================================
// Learning reason → tone mapping
// =============================================================================

const REASON_TONE: Record<string, string> = {
  holiday:
    'Focus on tourist scenarios: ordering food, asking for directions, booking hotels, sightseeing. Use a casual, friendly tone with travel vocabulary.',
  moving:
    'Focus on relocation scenarios: finding housing, registering at offices, setting up utilities, meeting neighbors. Use practical, daily-life vocabulary.',
  partner:
    "Focus on family and relationship contexts: meeting the partner's family, shared daily life, informal conversations. Use an informal register with everyday expressions.",
  work: 'Focus on professional communication: meetings, presentations, email correspondence, industry-specific terminology. Use a professional register.',
  general:
    'Use a balanced mix of everyday scenarios. Vary between casual and semi-formal registers.',
};

/**
 * Returns a tone instruction based on the learner's reason for learning.
 * Used in both the system prompt and block-level personalization.
 */
export function buildLearningReasonTone(reason: LearningReasonCode | null): string {
  if (!reason) return '';
  return REASON_TONE[reason] || '';
}

// =============================================================================
// Block-level personalization clause
// =============================================================================

/**
 * Builds a `## Personalization` prompt section for a single block.
 * This centralizes all personalization logic so prompt builders don't
 * duplicate it. Returns an empty string if no personalization data is set.
 *
 * @param profile - The full workbook profile
 * @param blockOrder - The block's position (1-based), used to distribute
 *   situations across blocks deterministically
 * @param isPreview - When true, uses stronger personalization wording so
 *   the preview showcases the "wow" factor with fewer blocks
 */
export function buildPersonalizationClause(
  profile: WorkbookProfile,
  blockOrder?: number,
  isPreview?: boolean,
): string {
  const sections: string[] = [];

  // Display name — always present (user input — wrapped for prompt armoring)
  const safeName = safeText(profile.display_name);
  sections.push(
    `- The learner's name is <user_input>${safeName}</user_input>. Use it naturally in exercises (as a character name in dialogues, in example sentences, etc.).`,
  );

  // Learning reason
  const reasonTone = buildLearningReasonTone(profile.learning_reason);
  if (reasonTone) {
    sections.push(`- **Learning reason:** ${reasonTone}`);
  }

  // Profession (user input — wrapped for prompt armoring)
  if (profile.profession) {
    const safeProfession = safeText(profile.profession);
    const professionInstruction = isPreview
      ? `- **Profession:** The learner works as a <user_input>${safeProfession}</user_input>. You MUST incorporate their professional context into this exercise — use work scenarios, industry-specific vocabulary, or colleague interactions that feel authentic to their field.`
      : `- **Profession:** The learner works as a <user_input>${safeProfession}</user_input>. Incorporate professional context where relevant (work scenarios, industry vocabulary, colleague interactions).`;
    sections.push(professionInstruction);
  }

  // Destination (user input — wrapped for prompt armoring)
  if (profile.destination_city || profile.destination_country) {
    const location = [profile.destination_city, profile.destination_country]
      .filter(Boolean)
      .map((v) => safeText(v))
      .join(', ');
    const destinationInstruction = isPreview
      ? `- **Destination:** The learner is interested in <user_input>${location}</user_input>. You MUST set this exercise in or around this location — reference local landmarks, regional customs, neighborhoods, or city-specific scenarios.`
      : `- **Destination:** The learner is interested in <user_input>${location}</user_input>. Set exercises in this location when possible (local landmarks, regional customs, city-specific scenarios).`;
    sections.push(destinationInstruction);
  }

  // Situations — pick 1-2 for this block based on blockOrder for deterministic distribution
  if (profile.situations && profile.situations.length > 0) {
    const situationDetails = profile.situations.map((code) => {
      const sit = SITUATIONS.find((s) => s.code === code);
      return sit ? `${sit.name} (${sit.examples.join(', ')})` : code;
    });

    if (blockOrder !== undefined && situationDetails.length > 1) {
      // Pick 1-2 situations for this specific block
      const idx = (blockOrder - 1) % situationDetails.length;
      const picked = [situationDetails[idx]];
      if (situationDetails.length > 2) {
        picked.push(situationDetails[(idx + 1) % situationDetails.length]);
      }
      sections.push(`- **Real-life situations to incorporate:** ${picked.join('; ')}`);
    } else {
      sections.push(`- **Real-life situations to incorporate:** ${situationDetails.join('; ')}`);
    }
  }

  if (sections.length <= 1) {
    // Only the name is set — still include it but keep it brief
    return `

## Personalization
${sections.join('\n')}`;
  }

  return `

## Personalization
${sections.join('\n')}`;
}
